import React, { useEffect, useState } from "react";
import { Alert, Box, Container, Skeleton } from "@mui/material";
import {
  useAddFanProfileComparisonMutation,
  useDeleteFanProfileComparisonMutation,
  useGetMyFanProfileQuery,
  usePatchFanProfileMutation,
  useReplaceFanProfileFollowsMutation,
  useReplaceFanProfileTeamsMutation,
} from "../store/api/mlbApi";
import { usePinDashboardWidget } from "../hooks/usePinDashboardWidget";
import FanHubHero from "../components/fanHub/FanHubHero";
import FanHubIdentityDialog from "../components/fanHub/FanHubIdentityDialog";
import { FanHubPlayerRail, FanHubTeamRail } from "../components/fanHub/FanHubRails";
import {
  FanHubComparisons,
  FanHubLineupGrid,
  FanHubPublished,
} from "../components/fanHub/FanHubBuilt";
import FanHubTrophyRoom from "../components/fanHub/FanHubTrophyRoom";
import {
  FanHubComparePicker,
  FanHubPlayerPicker,
  FanHubTeamPicker,
} from "../components/fanHub/FanHubPickers";
import { fanHubPageGlowSx, sportAccent } from "../components/fanHub/fanHubStyles";

const emptyIdentity = {
  username: "",
  displayName: "",
  avatar: "",
  preferredSport: "mlb",
};

const mutationMessage = (err, fallback) =>
  err?.data?.error || err?.message || fallback;

const Profile = () => {
  const { data, isLoading, error, refetch } = useGetMyFanProfileQuery();
  const [patchProfile] = usePatchFanProfileMutation();
  const [replaceTeams] = useReplaceFanProfileTeamsMutation();
  const [replaceFollows] = useReplaceFanProfileFollowsMutation();
  const [addComparison] = useAddFanProfileComparisonMutation();
  const [deleteComparison] = useDeleteFanProfileComparisonMutation();
  const { pinWidget, isPinning } = usePinDashboardWidget();

  const [identityForm, setIdentityForm] = useState(emptyIdentity);
  const [editOpen, setEditOpen] = useState(false);
  const [teamOpen, setTeamOpen] = useState(false);
  const [playerOpen, setPlayerOpen] = useState(false);
  const [compareOpen, setCompareOpen] = useState(false);
  const [notice, setNotice] = useState(null);

  useEffect(() => {
    if (!data?.identity) return;
    setIdentityForm({
      username: data.identity.username || "",
      displayName: data.identity.displayName || "",
      avatar: data.identity.avatar || "",
      preferredSport: data.identity.preferredSport || "mlb",
    });
  }, [
    data?.identity?.username,
    data?.identity?.displayName,
    data?.identity?.avatar,
    data?.identity?.preferredSport,
  ]);

  const flash = (text, severity = "success") => setNotice({ text, severity });

  const runWrite = async (work, fallback) => {
    try {
      await work();
      return true;
    } catch (err) {
      flash(mutationMessage(err, fallback), "error");
      return false;
    }
  };

  const pinIfNeeded = async (suggestion) => {
    if (!suggestion || suggestion.alreadyPinned) return;
    const result = await pinWidget(suggestion.widget);
    if (result.success) {
      flash("Pinned to dashboard");
      refetch();
    } else {
      flash(result.error || "Could not pin widget", "error");
    }
  };

  const saveIdentity = async () => {
    const ok = await runWrite(
      () =>
        patchProfile({
          username: identityForm.username,
          displayName: identityForm.displayName,
          avatar: identityForm.avatar || null,
          preferredSport: identityForm.preferredSport,
        }).unwrap(),
      "Could not save profile"
    );
    if (ok) {
      setEditOpen(false);
      flash("Identity saved");
    }
  };

  const setPreferredSport = async (preferredSport) => {
    if (preferredSport === identityForm.preferredSport) return;
    const previous = identityForm.preferredSport;
    setIdentityForm((prev) => ({ ...prev, preferredSport }));
    try {
      await patchProfile({ preferredSport }).unwrap();
    } catch (err) {
      setIdentityForm((prev) => ({ ...prev, preferredSport: previous }));
      flash(mutationMessage(err, "Could not update preferred sport"), "error");
    }
  };

  const addTeam = ({ sport, teamId }) =>
    runWrite(
      () =>
        replaceTeams([
          ...(data?.favoriteTeams || []).map((row) => ({
            sport: row.sport,
            teamId: row.teamId,
          })),
          { sport, teamId },
        ]).unwrap(),
      "Could not add team"
    );

  const removeTeam = (sport, teamId) =>
    runWrite(
      () =>
        replaceTeams(
          (data?.favoriteTeams || [])
            .filter((row) => !(row.sport === sport && row.teamId === teamId))
            .map((row) => ({ sport: row.sport, teamId: row.teamId }))
        ).unwrap(),
      "Could not remove team"
    );

  const addFollow = ({ sport, playerId }) =>
    runWrite(
      () =>
        replaceFollows([
          ...(data?.followedPlayers || []).map((row) => ({
            sport: row.sport,
            playerId: row.playerId,
          })),
          { sport, playerId },
        ]).unwrap(),
      "Could not follow player"
    );

  const removeFollow = (sport, playerId) =>
    runWrite(
      () =>
        replaceFollows(
          (data?.followedPlayers || [])
            .filter((row) => !(row.sport === sport && row.playerId === playerId))
            .map((row) => ({ sport: row.sport, playerId: row.playerId }))
        ).unwrap(),
      "Could not unfollow player"
    );

  const saveComparison = (payload) =>
    runWrite(() => addComparison(payload).unwrap(), "Could not save comparison");

  const removeComparison = (id) =>
    runWrite(() => deleteComparison(id).unwrap(), "Could not remove comparison");

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Skeleton variant="rounded" height={220} />
      </Container>
    );
  }

  if (error || !data) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error">Could not load your profile.</Alert>
      </Container>
    );
  }

  const accent = sportAccent(data.identity?.preferredSport);

  return (
    <Box sx={fanHubPageGlowSx(accent)}>
      <Container maxWidth="md" sx={{ mt: { xs: 2, sm: 4 }, position: "relative" }}>
        {notice && (
          <Alert
            severity={notice.severity}
            sx={{ mb: 2 }}
            onClose={() => setNotice(null)}
          >
            {notice.text}
          </Alert>
        )}
        <FanHubHero
          identity={data.identity}
          onEdit={() => setEditOpen(true)}
          onPreferredSport={setPreferredSport}
        />
        <FanHubTeamRail
          teams={data.favoriteTeams}
          onAdd={() => setTeamOpen(true)}
          onRemove={removeTeam}
        />
        <FanHubPlayerRail
          players={data.followedPlayers}
          suggestions={data.suggestions}
          onAdd={() => setPlayerOpen(true)}
          onRemove={removeFollow}
          onPin={pinIfNeeded}
          isPinning={isPinning}
        />
        <FanHubLineupGrid lineups={data.built?.lineups} />
        <FanHubPublished rosters={data.built?.publishedRosters} />
        <FanHubComparisons
          comparisons={data.savedComparisons}
          suggestions={data.suggestions}
          onAdd={() => setCompareOpen(true)}
          onPin={pinIfNeeded}
          onRemove={removeComparison}
          isPinning={isPinning}
        />
        <FanHubTrophyRoom
          trophyRoom={data.trophyRoom}
          preferredSport={data.identity.preferredSport}
        />
      </Container>

      <FanHubIdentityDialog
        open={editOpen}
        values={identityForm}
        onChange={(patch) => setIdentityForm((prev) => ({ ...prev, ...patch }))}
        onClose={() => setEditOpen(false)}
        onSave={saveIdentity}
      />
      <FanHubTeamPicker
        open={teamOpen}
        onClose={() => setTeamOpen(false)}
        onSelect={addTeam}
      />
      <FanHubPlayerPicker
        open={playerOpen}
        onClose={() => setPlayerOpen(false)}
        onSelect={addFollow}
      />
      <FanHubComparePicker
        open={compareOpen}
        onClose={() => setCompareOpen(false)}
        onSave={saveComparison}
      />
    </Box>
  );
};

export default Profile;
